import {
  combineReducers,
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import LoadingStatus from '@libs/loading-status';

import { RootState } from '@app/store/types';

import { Label } from '../label/types';
import DocumentStatus from './types/DocumentStatus';
import { Signer } from './types/ExternalDocument';

interface DocumentPeriod {
  from: string;
  to: string | null;
}

interface PersonInCharge {
  name: string;
  email: string | null;
  phone: string | null;
}

interface Renewal {
  noticeBeforeExpiration: {
    value: 30 | 60 | 90 | 120;
    unit: 'day';
  } | null; // 계약 종료일 기준 N일 전까지 통지
  period: {
    value: number;
    unit: 'year';
  }; // 갱신 주기
}

interface DocumentMetadata {
  type?: 'PAPER' | 'ELECTRONIC';
  dateOfCompletion?: string;
  documentPeriod?: DocumentPeriod;
  status?: DocumentStatus;
  renewal?: Renewal;
  personInCharge?: PersonInCharge;
  signers?: Signer[];
  originalLocation?: string;
  memo?: string;
  labels: Label[];
}

export interface DocumentFile {
  name: string;
  status: LoadingStatus;
  file: File;
  isContract: boolean;
  fileKey: string | null;
}

export interface DocumentFileWithFileKey extends DocumentFile {
  fileKey: string;
}

interface Document {
  status: LoadingStatus;
  name: string;
  files: Array<DocumentFile>;
  metadata?: DocumentMetadata;
  error?: Error;
}

interface ExternalDocumentUploadState {
  isModalMinimized: boolean;
  showManagementTooltip: boolean;
  documents: Array<Document>;
}

const initialState: ExternalDocumentUploadState = {
  isModalMinimized: false,
  showManagementTooltip: false,
  documents: [],
};

const uploadSlice = createSlice({
  name: 'externalDocumentUpload',
  initialState,
  reducers: {
    startUpload: (
      state,
      action: PayloadAction<{
        documents: Array<{
          name: string;
          files: Array<{ name: string; file: File; isContract: boolean }>;
          metadata?: DocumentMetadata;
        }>;
      }>
    ) => {
      state.documents = action.payload.documents.map((doc) => ({
        status: LoadingStatus.IDLE,
        name: doc.name,
        files: doc.files.map((f) => ({
          name: f.name,
          status: LoadingStatus.IDLE,
          isContract: f.isContract,
          file: f.file,
          fileKey: null,
        })),
        metadata: doc.metadata,
      }));
      state.showManagementTooltip = true;
      state.isModalMinimized = false;
    },
    fileUploadStart: (state, action: PayloadAction<{ docIdx: number; fileIdx: number }>) => {
      const { docIdx, fileIdx } = action.payload;
      const doc = state.documents[docIdx];
      doc.files[fileIdx].status = LoadingStatus.PENDING;
    },
    fileUploadFulfilled: (
      state,
      action: PayloadAction<{ docIdx: number; fileIdx: number; fileKey: string }>
    ) => {
      const { docIdx, fileIdx, fileKey } = action.payload;
      const doc = state.documents[docIdx];
      doc.files[fileIdx].status = LoadingStatus.SUCCEEDED;
      doc.files[fileIdx].fileKey = fileKey;
    },
    fileUploadRejected: (
      state,
      action: PayloadAction<{ docIdx: number; fileIdx: number; error: Error }>
    ) => {
      const { docIdx, fileIdx, error } = action.payload;
      state.documents[docIdx].files[fileIdx].status = LoadingStatus.FAILED;
      state.documents[docIdx].status = LoadingStatus.FAILED;
      state.documents[docIdx].error = error;
    },
    documentUploadStart: (state, action: PayloadAction<{ docIdx: number }>) => {
      const { docIdx } = action.payload;
      const doc = state.documents[docIdx];
      doc.status = LoadingStatus.PENDING;
    },
    documentUploadFulfilled: (state, action: PayloadAction<{ docIdx: number }>) => {
      const { docIdx } = action.payload;
      const doc = state.documents[docIdx];
      doc.status = LoadingStatus.SUCCEEDED;
    },
    documentUploadRejected: (state, action: PayloadAction<{ docIdx: number; error: Error }>) => {
      const { docIdx, error } = action.payload;
      const doc = state.documents[docIdx];
      doc.status = LoadingStatus.FAILED;
      doc.error = error;
    },
    initializeExternalDocumentUpload: (state) => ({
      ...initialState,
      showManagementTooltip: state.showManagementTooltip,
    }),
    retryFailedDocuments: (state) => {
      state.documents
        .filter((d) => d.status === LoadingStatus.FAILED)
        .forEach((doc) => {
          doc.status = LoadingStatus.IDLE;
          doc.error = undefined;
          doc.files.forEach((file) => {
            if (file.status === LoadingStatus.FAILED || !file.fileKey) {
              file.status = LoadingStatus.IDLE;
              file.fileKey = null;
            }
          });
        });
    },
    toggleModalMinimize: (state) => {
      state.isModalMinimized = !state.isModalMinimized;
    },
    confirmManagementTooltip: (state) => {
      state.showManagementTooltip = false;
    },
  },
});

const batchUpdateJobsEntityAdapter = createEntityAdapter<{
  jobId: string;
  count: {
    pending: number;
    success: number;
    failure: number;
  };
}>({
  selectId: (entity) => entity.jobId,
});

const batchUpdateJobsEntitySelectors = batchUpdateJobsEntityAdapter.getSelectors();

const batchUpdateSlice = createSlice({
  name: 'externalDocumentUploadBatchUpdate',
  initialState: batchUpdateJobsEntityAdapter.getInitialState(),
  reducers: {
    batchUpdateStarted: (state, action: PayloadAction<{ jobId: string }>) => {
      const { jobId } = action.payload;
      return batchUpdateJobsEntityAdapter.addOne(state, {
        jobId,
        count: {
          pending: 0,
          success: 0,
          failure: 0,
        },
      });
    },
    updateBatchJobStatus: (
      state,
      action: PayloadAction<{
        jobId: string;
        count: {
          pending: number;
          success: number;
          failure: number;
        };
      }>
    ) => {
      const { jobId, count } = action.payload;
      return batchUpdateJobsEntityAdapter.updateOne(state, {
        id: jobId,
        changes: {
          count,
        },
      });
    },
    initializeExternalDocumentBatchJob: () => batchUpdateJobsEntityAdapter.getInitialState(),
  },
});

export const reducer = combineReducers({
  upload: uploadSlice.reducer,
  batchUpdate: batchUpdateSlice.reducer,
});

export const {
  initializeExternalDocumentUpload,
  documentUploadFulfilled,
  documentUploadRejected,
  documentUploadStart,
  fileUploadFulfilled,
  fileUploadRejected,
  fileUploadStart,
  retryFailedDocuments,
  startUpload,
  toggleModalMinimize,
  confirmManagementTooltip,
} = uploadSlice.actions;

export const { batchUpdateStarted, initializeExternalDocumentBatchJob, updateBatchJobStatus } =
  batchUpdateSlice.actions;

const getExternalDocumentUpload = (state: RootState) => state.externalDocument.upload;
const getDocuments = (state: RootState) => getExternalDocumentUpload(state).documents;

export const getDocumentByIndex = (state: RootState, docIdx: number) =>
  getExternalDocumentUpload(state).documents[docIdx];
export const getFileByIndex = (state: RootState, docIdx: number, fileIdx: number) =>
  getExternalDocumentUpload(state).documents[docIdx].files[fileIdx];

// 문서 자체가 업로드 중이지 않아도
// 문서의 어떤 파일이 업로드 중일때 해당 문서가 업로드 중이라고 계산합니다.
function calculateDocumentTotalStatus(d: Document) {
  let status: LoadingStatus;

  if (d.status === LoadingStatus.IDLE) {
    if (d.files.every((f) => f.status === LoadingStatus.IDLE)) {
      status = LoadingStatus.IDLE;
    } else {
      status = LoadingStatus.PENDING;
    }
  } else {
    status = d.status;
  }
  return status;
}

// 뷰에서 소비하는 문서 상태를 반환합니다.
export const getExternalDocumentUploadingDocuments = createSelector(getDocuments, (documents) =>
  documents.map((document) => {
    return {
      status: calculateDocumentTotalStatus(document),
      name: document.name,
      error: document.error,
      labels: document.metadata?.labels || [],
    };
  })
);

export const getNextDocumentToUpload = createSelector(getDocuments, (documents) => {
  const docIdx = documents.findIndex(
    (document) =>
      document.status === LoadingStatus.IDLE &&
      document.files.every((file) => file.status === LoadingStatus.SUCCEEDED)
  );

  if (docIdx === -1) {
    return null;
  }

  return { ...documents[docIdx], idx: docIdx };
});

export const getNextFileToUpload = createSelector(getDocuments, (documents) => {
  for (let i = 0; i < documents.length; i += 1) {
    const document = documents[i];
    if (document.status === LoadingStatus.PENDING || document.status === LoadingStatus.IDLE) {
      const fileIdx = document.files.findIndex((file) => file.status === LoadingStatus.IDLE);
      if (fileIdx !== -1) {
        return { ...document.files[fileIdx], docIdx: i, fileIdx };
      }
    }
  }

  return null;
});

export const getUploadingSummary = createSelector(getDocuments, (documents) =>
  documents.reduce(
    (acc, document) => {
      switch (document.status) {
        case LoadingStatus.SUCCEEDED:
          acc.succeeded += 1;
          break;
        case LoadingStatus.FAILED:
          acc.failed += 1;
          break;
        case LoadingStatus.IDLE:
          acc.idle += 1;
          break;
        case LoadingStatus.PENDING:
          acc.pending += 1;
          break;
        default:
      }
      return acc;
    },
    { failed: 0, succeeded: 0, idle: 0, pending: 0 }
  )
);

function calculateTotalUploadState(
  documents: Document[]
): LoadingStatus.IDLE | LoadingStatus.PENDING | LoadingStatus.SUCCEEDED {
  if (documents.length === 0) {
    return LoadingStatus.IDLE;
  }

  if (
    documents.some((document) =>
      [LoadingStatus.PENDING, LoadingStatus.IDLE].includes(calculateDocumentTotalStatus(document))
    )
  ) {
    return LoadingStatus.PENDING;
  }

  return LoadingStatus.SUCCEEDED;
}

// 현재 업로드 상황을 반환합니다. 업로드 전이면 IDLE, 업로드 중이면 PENDING, 업로드 완료(실패한 문서가 있어도)면 SUCCEEDED를 반환합니다.
export const getExternalDocumentUploadStatus = createSelector(
  getDocuments,
  (documents): LoadingStatus.IDLE | LoadingStatus.PENDING | LoadingStatus.SUCCEEDED =>
    calculateTotalUploadState(documents)
);
export const getIsNotIdleStatus = (state: RootState) =>
  getExternalDocumentUploadStatus(state) !== LoadingStatus.IDLE;

export const getIsModalMinimized = (state: RootState) =>
  getExternalDocumentUpload(state).isModalMinimized;

const getExternalDocumentBatchUpdate = (state: RootState) => state.externalDocument.batchUpdate;
export const getExternalDocumentTotalBatchUpdateCount = (
  state: RootState
): { pending: number; success: number; failure: number } | undefined => {
  const batchUpdate = getExternalDocumentBatchUpdate(state);
  const jobs = batchUpdateJobsEntitySelectors.selectAll(batchUpdate);

  if (jobs.length === 0) {
    return undefined;
  }

  return jobs.reduce(
    (acc, job) => ({
      pending: acc.pending + job.count.pending,
      success: acc.success + job.count.success,
      failure: acc.failure + job.count.failure,
    }),
    { pending: 0, success: 0, failure: 0 }
  );
};

export const getExternalDocumentBatchUpdateCount = (
  state: RootState
): { pending: number; success: number; failure: number } | undefined => {
  const batchUpdate = getExternalDocumentBatchUpdate(state);
  const jobs = batchUpdateJobsEntitySelectors.selectAll(batchUpdate);

  if (jobs.length === 0) {
    return undefined;
  }

  return jobs.reduce(
    (acc, job) => ({
      pending: acc.pending + job.count.pending,
      success: acc.success + job.count.success,
      failure: acc.failure + job.count.failure,
    }),
    { pending: 0, success: 0, failure: 0 }
  );
};

export const getShowManagementTooltip = (state: RootState) =>
  getExternalDocumentUpload(state).showManagementTooltip;
